import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const usePACMembershipData = () => {
  const user = useSelector((state) => state.auth.user) || {};

  const { alumnis = [] } = user || {};

  const status = useMemo(() => {
    const isApproved = alumnis.some(
      (alumni) => alumni.pacMemberAt && alumni.pacMemberApprovedBy,
    );

    if (isApproved) {
      return 'APPROVED';
    }

    const isRevoked = alumnis.every(
      (alumni) => !alumni.pacMemberAt && alumni.pacMemberApprovedBy,
    );

    if (isRevoked) {
      return 'REVOKED';
    }

    const isPending = alumnis.some(
      (alumni) =>
        alumni.pacMemberRequestedAt &&
        !alumni.pacMemberAt &&
        !alumni.pacMemberApprovedBy,
    );

    if (isPending) {
      return 'PENDING';
    }

    return '';
  }, [alumnis]);

  const userStatus = useMemo(() => {
    const isStudent = user?.attendees?.length > 0;

    const isAlumni = alumnis?.length > 0;

    const isEligibleForAlumniFeature =
      Array.isArray(alumnis) && alumnis.some((alumni) => alumni.pcnMemberAt);

    const isPACMember = isAlumni && status === 'APPROVED';

    return { isStudent, isAlumni, isPACMember, isEligibleForAlumniFeature };
  }, [alumnis, status, user?.attendees?.length]);

  const jobSeekingStatus = useMemo(() => {
    if (!Array.isArray(alumnis)) return '';

    const latestAlumnis = Object.values(
      [...alumnis]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .reduce((acc, alumni) => {
          if (!acc[alumni.userId]) {
            acc[alumni.userId] = alumni;
          }
          return acc;
        }, {}),
    );

    const activeStatus = latestAlumnis.some(
      (alumni) => alumni.jobSeekingStatus === 'ACTIVE',
    );
    const onHoldStatus = latestAlumnis.some(
      (alumni) => alumni.jobSeekingStatus === 'ON HOLD',
    );
    const passiveStatus = latestAlumnis.every(
      (alumni) => alumni.jobSeekingStatus === 'PASSIVE',
    );

    if (activeStatus) return 'ACTIVE';
    if (onHoldStatus) return 'ON HOLD';
    if (passiveStatus) return 'PASSIVE';

    return '';
  }, [alumnis]);

  return { status, alumnis, userStatus, jobSeekingStatus };
};

export default usePACMembershipData;
